import { API_BASE_URL } from "../config/apiConfig";
import { URL_PREFIX } from "../constants/stores";

/* =========================================================
   SHARED
========================================================= */

export interface Pagination {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
}

export interface UserSummary {
    _id: string;
    first_name: string;
    last_name: string;
    email: string;
    location: string;
    full_name: string;
}

export interface ForkliftUser extends UserSummary {
    driver_key: string;
}

export interface BaseAllocation {
    allocation_id: number;

    po_no: string | null;

    style_no: string | null;
    description?: string | null;
    color?: string | null;

    storeName?: string[] | null;

    order_status?: string | null;

    updatedAt?: string | null;
}

/* =========================================================
   OPERATIONS OVERVIEW
========================================================= */

export interface ForkliftDailySummary {
    completed_today: number;
    active_drivers_today: number;
    average_pick_minutes_today: number | string | null;
}

export interface FloorDailySummary {
    completed_styles_today: number;
    active_floor_users_today: number;
    stores_completed_today: number;
    average_floor_minutes_today: number | string | null;
}

export interface PendingSummary {
    forklift_pending: number;
    new_product_floor_pending: number;
    restock_floor_pending: number;
}

export interface LatestForkliftAllocation extends BaseAllocation {
    user_id: string;

    first_name: string;
    last_name: string;
    full_name: string;

    pick_started_at: string | null;
    pick_completed_at: string | null;

    pick_duration_seconds: number | null;
}

export interface LatestFloorAllocation extends BaseAllocation {
    user_id: string;

    first_name: string;
    last_name: string;
    full_name: string;

    initial: string[] | null;
}

export interface OperationsStatsResponse {
    forklift: {
        summary: ForkliftDailySummary;
        users: ForkliftUser[];
        latestByDriver: LatestForkliftAllocation[];
    };

    floor: {
        summary: FloorDailySummary;
        users: UserSummary[];
        latestByUser: LatestFloorAllocation[];
    };

    pending: PendingSummary;
}

/* =========================================================
   FORKLIFT DRIVER DETAIL
========================================================= */

export interface ForkliftDriverSummary {
    today_count: number;
    week_count: number;
    month_count: number;
    all_time_count: number;

    weekly_average_minutes: number | string | null;
    monthly_average_minutes: number | string | null;
    all_time_average_minutes: number | string | null;

    picked_count: number;
    picked_all_count: number;
    not_found_count: number;

    last_completed_at: string | null;
}

export interface ForkliftAllocationDetail extends BaseAllocation {
    pickedQty?: {
        size: string;
        quantity: number;
    }[];

    pick_started_at: string | null;
    pick_completed_at: string | null;

    pick_duration_seconds: number | null;
}

export interface ForkliftDriverStatsResponse {
    user: ForkliftUser;

    summary: ForkliftDriverSummary;

    allocations: ForkliftAllocationDetail[];

    pagination: Pagination;
}



/* =========================================================
   FLOOR ACTIVITY
========================================================= */
export interface OrderActivityStyle {
    allocation_id: number;

    style_id: number | null;
    style_no: string | null;

    description: string | null;
    color: string | null;

    driver: string | null;
    lead: string | null;

    order_status: string | null;

    storeName: string[] | null;

    pick_started_at: string | null;
    pick_completed_at: string | null;

    updatedAt: string;
}

export interface OrderActivity {
    po_id: number;
    po_no: string;
    supplier_name: string;

    style_count: number;

    forklift_started_count: number;
    forklift_completed_count: number;
    not_found_count: number;

    first_pick_started_at: string | null;
    last_pick_completed_at: string | null;
    last_updated_at: string | null;
    last_activity_at: string | null;

    drivers: string[];
    floor_users: string[];

    styles: OrderActivityStyle[];
}

export interface OrderActivityResponse {
    orders: OrderActivity[];

    pagination: Pagination;

    range: {
        from: string | null;
        to: string | null;
        defaultToday: boolean;
    };
}

export interface OrderActivityParams {
    page?: number;
    size?: number;

    from?: string;
    to?: string;
}
/* =========================================================
   FLOOR USER DETAIL
========================================================= */

export interface FloorUserSummary {
    today_count: number;
    week_count: number;
    month_count: number;
    all_time_count: number;

    stores_completed: number;

    weekly_average_minutes: number | string | null;
    monthly_average_minutes: number | string | null;
    all_time_average_minutes: number | string | null;

    last_completed_at: string | null;
}

export interface FloorAllocationDetail extends BaseAllocation {
    initial: string[] | null;

    estimated_started_at: string | null;
    completed_at: string | null;

    floor_duration_seconds: number | null;
}

export interface FloorUserStatsResponse {
    user: UserSummary;

    summary: FloorUserSummary;

    allocations: FloorAllocationDetail[];

    pagination: Pagination;
}

/* =========================================================
   FILTERS
========================================================= */

export interface PersonStatsParams {
    page?: number;
    size?: number;

    completedFrom?: string;
    completedTo?: string;
}

/* =========================================================
   HELPERS
========================================================= */

const buildStatsSearchParams = (
    params: PersonStatsParams = {}
) => {
    const searchParams = new URLSearchParams();

    searchParams.set(
        "page",
        String(params.page || 1)
    );

    searchParams.set(
        "size",
        String(params.size || 25)
    );

    if (params.completedFrom) {
        searchParams.set(
            "completedFrom",
            params.completedFrom
        );
    }

    if (params.completedTo) {
        searchParams.set(
            "completedTo",
            params.completedTo
        );
    }

    return searchParams;
};

const fetchStats = async <T>(
    url: string,
    token: string
): Promise<T> => {
    const response = await fetch(url, {
        method: "GET",

        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    });

    const result = await response.json();

    if (!response.ok) {
        throw new Error(
            result?.message ||
            "Unable to load stats."
        );
    }

    return result.data as T;
};

/* =========================================================
   API
========================================================= */

/**
 * Load overall DC operations dashboard.
 *
 * Location is resolved by the backend from req.user.
 */
export const getOperationsStats = async (
    token: string
): Promise<OperationsStatsResponse> => {
    return fetchStats<OperationsStatsResponse>(
        `${URL_PREFIX}/stats/operationsStats`,
        token
    );
};

/**
 * Load detailed forklift stats for one Forklift user.
 */
export const getForkliftDriverStats = async (
    token: string,
    userId: string,
    params: PersonStatsParams = {}
): Promise<ForkliftDriverStatsResponse> => {
    const searchParams =
        buildStatsSearchParams(params);

    return fetchStats<ForkliftDriverStatsResponse>(
        `${URL_PREFIX}/stats/operationsStats/forklift/${userId}?${searchParams.toString()}`,
        token
    );
};

/**
 * Load detailed floor stats for one Admin user.
 */
export const getFloorUserStats = async (
    token: string,
    userId: string,
    params: PersonStatsParams = {}
): Promise<FloorUserStatsResponse> => {
    const searchParams =
        buildStatsSearchParams(params);

    return fetchStats<FloorUserStatsResponse>(
        `${URL_PREFIX}/stats/operationsStats/floor/${userId}?${searchParams.toString()}`,
        token
    );
};

export const getOrderActivityStats = async (
    token: string,
    params: OrderActivityParams = {}
): Promise<OrderActivityResponse> => {
    const searchParams =
        new URLSearchParams();

    searchParams.set(
        "page",
        String(params.page || 1)
    );

    searchParams.set(
        "size",
        String(params.size || 25)
    );

    if (params.from) {
        searchParams.set(
            "from",
            params.from
        );
    }

    if (params.to) {
        searchParams.set(
            "to",
            params.to
        );
    }

    return fetchStats<OrderActivityResponse>(
        `${URL_PREFIX}/stats/operationsStats/orders?${searchParams.toString()}`,
        token
    );
};